import { useEffect, useState } from 'react';
import { useAuth } from 'zite-auth-sdk';
import { getStaffDashboard, GetStaffDashboardOutputType } from 'zite-endpoints-sdk';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Link } from 'react-router-dom';
import {
  AlertTriangle, Clock, Calendar, CheckSquare, ChevronRight, ShieldAlert,
  Megaphone, FileEdit, BookOpen, Flame, ClipboardCheck, Users, Award,
} from 'lucide-react';

type Action = GetStaffDashboardOutputType['actions'][0];

const typeIcons: Record<string, React.FC<{ className?: string }>> = {
  compliance: ShieldAlert, certification: Award, announcement: Megaphone,
  edit_request: FileEdit, onboarding: Users, policy: ClipboardCheck,
  signoff: ClipboardCheck, task: CheckSquare, fire: Flame, training: BookOpen,
};

const urgencyStyles: Record<string, string> = {
  overdue: 'border-l-destructive bg-destructive/5',
  today: 'border-l-primary bg-primary/5',
  due_soon: 'border-l-accent bg-accent/5',
  waiting: 'border-l-muted-foreground bg-muted/50',
  info: 'border-l-border bg-muted/30',
};

const urgencyLabels: Record<string, string> = {
  overdue: '🔴 Overdue', today: '🟡 Today', due_soon: '🟢 Due Soon', waiting: '⏳ Waiting', info: 'ℹ️ Info',
};

function ActionRow({ action }: { action: Action }) {
  const Icon = typeIcons[action.type] || CheckSquare;
  return (
    <Link to={action.link} className={`flex items-center gap-3 border border-border border-l-4 rounded-lg px-4 py-3 hover:bg-muted/50 transition-colors no-underline ${urgencyStyles[action.urgency] || ''}`}>
      <Icon className="w-4.5 h-4.5 shrink-0 text-muted-foreground" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-foreground">{action.title}</p>
        {action.subtitle && <p className="text-xs text-muted-foreground mt-0.5">{action.subtitle}</p>}
        {action.dueDate && <p className="text-xs text-muted-foreground mt-0.5">Due: {action.dueDate}</p>}
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {action.status && <span className="text-xs bg-muted px-2 py-0.5 rounded text-muted-foreground">{action.status}</span>}
        <ChevronRight className="w-4 h-4 text-muted-foreground" />
      </div>
    </Link>
  );
}

export default function ActionQueue() {
  const { user } = useAuth();
  const [data, setData] = useState<GetStaffDashboardOutputType | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    getStaffDashboard({}).then(setData).catch(console.error).finally(() => setLoading(false));
  }, [user]);

  if (loading) return (
    <div className="px-4 md:px-8 py-6 max-w-4xl mx-auto space-y-4">
      <Skeleton className="h-8 w-48" />
      {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-16" />)}
    </div>
  );

  const actions = data?.actions || [];
  const overdue = actions.filter(a => a.urgency === 'overdue');
  const todayActions = actions.filter(a => a.urgency === 'today');
  const dueSoon = actions.filter(a => a.urgency === 'due_soon');
  const waiting = actions.filter(a => a.urgency === 'waiting');
  const activeCount = overdue.length + todayActions.length + dueSoon.length;

  return (
    <div className="px-4 md:px-8 py-6 max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold text-foreground">Action Queue</h1>
          <p className="text-sm text-muted-foreground mt-0.5">{activeCount} active action{activeCount !== 1 ? 's' : ''} · {waiting.length} waiting</p>
        </div>
      </div>

      <Tabs defaultValue="active">
        <TabsList className="mb-4">
          <TabsTrigger value="active" className="gap-1.5">
            <AlertTriangle className="w-3.5 h-3.5" /> Active
            {activeCount > 0 && <span className="ml-1 bg-destructive text-destructive-foreground text-xs rounded-full w-5 h-5 flex items-center justify-center">{activeCount}</span>}
          </TabsTrigger>
          <TabsTrigger value="waiting" className="gap-1.5"><Clock className="w-3.5 h-3.5" /> Waiting ({waiting.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="active" className="space-y-4">
          {overdue.length > 0 && (
            <div>
              <h3 className="text-xs font-semibold text-destructive uppercase tracking-wider mb-2">{urgencyLabels.overdue} ({overdue.length})</h3>
              <div className="space-y-2">{overdue.map(a => <ActionRow key={a.id} action={a} />)}</div>
            </div>
          )}
          {todayActions.length > 0 && (
            <div>
              <h3 className="text-xs font-semibold text-primary uppercase tracking-wider mb-2">{urgencyLabels.today} ({todayActions.length})</h3>
              <div className="space-y-2">{todayActions.map(a => <ActionRow key={a.id} action={a} />)}</div>
            </div>
          )}
          {dueSoon.length > 0 && (
            <div>
              <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">{urgencyLabels.due_soon} ({dueSoon.length})</h3>
              <div className="space-y-2">{dueSoon.map(a => <ActionRow key={a.id} action={a} />)}</div>
            </div>
          )}
          {activeCount === 0 && (
            <div className="text-center py-12 border border-dashed border-border rounded-lg">
              <CheckSquare className="w-8 h-8 text-primary mx-auto mb-2 opacity-40" />
              <p className="text-sm text-muted-foreground">All clear — no outstanding actions!</p>
            </div>
          )}
        </TabsContent>

        <TabsContent value="waiting" className="space-y-2">
          {waiting.length === 0 ? (
            <div className="text-center py-12 border border-dashed border-border rounded-lg">
              <Clock className="w-8 h-8 text-muted-foreground mx-auto mb-2 opacity-40" />
              <p className="text-sm text-muted-foreground">Nothing waiting on anyone else right now.</p>
            </div>
          ) : waiting.map(a => <ActionRow key={a.id} action={a} />)}
        </TabsContent>
      </Tabs>
    </div>
  );
}
